import IconResponsibleAdults from "../assets/images/navigation/responsibleAdults.svg?react";
import IconStudentDetails from "../assets/images/navigation/studentDetails.svg?react";
import IconEnrolmentAgreement from "../assets/images/navigation/enrolmentAgreement.svg?react";
import IconPreviousEducation from "../assets/images/navigation/previousEducation.svg?react";
import IconTuitionBilling from "../assets/images/navigation/tuitionBilling.svg?react";
import { isVersionUK, isVersionUS } from "../services/versionService";
import { KEY_NULL } from "../utils/getVerificationStatusKey";

export const TASK_KEYS = {
    RESPONSIBLE_ADULTS: "responsibleAdults",
    STUDENT_DETAILS: "studentDetails",
    PREVIOUS_EDUCATION: "previousEducation",
    ENROLMENT_AGREEMENT: "enrolmentAgreement",
    COURSE_SELECTION: "courseSelection",
    CONSULTATION_CALL: "consultationCall",
    TUITION_BILLING: "tuitionBilling",
};

export const HIDDEN_FOR_US_FULL_TIME = [
    TASK_KEYS.CONSULTATION_CALL,
    TASK_KEYS.COURSE_SELECTION,
];

export const applicantTasksHelper = {
    createResponsibleAdultsTask: (statuses, isLockedStatus) => ({
        key: TASK_KEYS.RESPONSIBLE_ADULTS,
        titleKey: "navigation.responsibleAdults",
        icon: IconResponsibleAdults,
        isLocked: isLockedStatus.responsibleAdults,
        isWarning: statuses.responsibleAdultsDetails.isWarning || statuses.responsibleAdults.isWarning,
        isSuccess: statuses.responsibleAdultsDetails.isSuccess && statuses.responsibleAdults.isSuccess,
        tasks: [
            {
                titleKey: "tasks.parentDetailsAdded",
                statusKey: statuses.responsibleAdultsDetails.statusTextKey,
                ...statuses.responsibleAdultsDetails,
            },
            {
                titleKey: "tasks.verifyAddress",
                statusKey: statuses.responsibleAdults.statusTextKey,
                ...statuses.responsibleAdults,
            },
        ],
    }),

    createStudentDetailsTask: (statuses, isLockedStatus, generalStatuses, isRequiredOutOfYear, isRequiredDBS) => {
        const studentDetailsTask = {
            key: TASK_KEYS.STUDENT_DETAILS,
            titleKey: "navigation.studentDetails",
            icon: IconStudentDetails,
            isLocked: isLockedStatus.studentDetails,
            isWarning: statuses.studentAddress.isWarning
                || statuses.studentVerification.isWarning
                || statuses.outOfYearFormUpload.isWarning
                || statuses.dbsUpload.isWarning
                || statuses.safeguardingUpload.isWarning,
            isSuccess: statuses.studentAddress.isSuccess
                && statuses.studentVerification.isSuccess
                && statuses.outOfYearFormUpload.isSuccess
                && statuses.dbsUpload.isSuccess
                && statuses.safeguardingUpload.isSuccess,
            tasks: [
                {
                    titleKey: "tasks.studentAddressAdded",
                    statusKey: statuses.studentAddress.statusTextKey,
                    ...statuses.studentAddress,
                },
                {
                    titleKey: "tasks.verifyStudentsID",
                    statusKey: statuses.studentVerification.statusTextKey,
                    ...statuses.studentVerification,
                },
            ],
        };

        if (isRequiredOutOfYear) {
            studentDetailsTask.tasks.push({
                titleKey: "tasks.outOfYearFormUpload",
                statusKey: statuses.outOfYearFormUpload.statusTextKey,
                ...statuses.outOfYearFormUpload,
            });
        }

        if (isRequiredDBS) {
            studentDetailsTask.tasks.push({
                titleKey: "tasks.dbsUpload",
                statusKey: statuses.dbsUpload.statusTextKey,
                ...statuses.dbsUpload,
            });
        }

        if (generalStatuses.student?.hasSafeguardingNeed) {
            studentDetailsTask.tasks.push({
                titleKey: "tasks.safeguardingUpload",
                statusKey: statuses.safeguardingUpload.statusTextKey,
                ...statuses.safeguardingUpload,
            });
        }

        return studentDetailsTask;
    },

    createPreviousEducationTask: (statuses, isLockedStatus, generalStatuses, isSchoolReportsRequired) => {
        const previousEducation = {
            key: TASK_KEYS.PREVIOUS_EDUCATION,
            titleKey: "navigation.previousEducation",
            icon: IconPreviousEducation,
            isLocked: isLockedStatus.previousEducation,
            isWarning: statuses.previousEducation.isWarning
                || statuses.transcripts.isWarning
                || statuses.ibApplicationLetter.isWarning
                || statuses.ibGradeEvidence.isWarning,
            isSuccess: statuses.previousEducation.isSuccess
                && statuses.transcripts.isSuccess
                && statuses.ibApplicationLetter.isSuccess
                && statuses.ibGradeEvidence.isSuccess,
            tasks: [
                {
                    titleKey: "tasks.previousEducationAdded",
                    statusKey: statuses.previousEducation.statusTextKey,
                    ...statuses.previousEducation,
                },
            ],
        };

        if (isSchoolReportsRequired || isVersionUS) {
            previousEducation.tasks.push({
                titleKey: "tasks.schoolReports",
                statusKey: statuses.transcripts.statusTextKey,
                ...statuses.transcripts,
            });
        }

        if (generalStatuses.student?.studyingIb) {
            previousEducation.tasks.push({
                titleKey: "tasks.ibApplicationLetter",
                statusKey: statuses.ibApplicationLetter.statusTextKey,
                ...statuses.ibApplicationLetter,
            });
            previousEducation.tasks.push({
                titleKey: "tasks.ibGradeEvidence",
                statusKey: statuses.ibGradeEvidence.statusTextKey,
                ...statuses.ibGradeEvidence,
            });
        }

        return previousEducation;
    },

    createEnrolmentAgreementTask: (statuses, isLockedStatus) => ({
        key: TASK_KEYS.ENROLMENT_AGREEMENT,
        titleKey: "navigation.enrolmentAgreement",
        icon: IconEnrolmentAgreement,
        isDisabled: isLockedStatus.enrolmentAgreement,
        isLocked: isLockedStatus.enrolmentAgreement,
        isWarning: statuses.enrolmentAgreement.isWarning,
        isSuccess: statuses.enrolmentAgreement.isSuccess,
        tasks: [
            {
                titleKey: "tasks.contractSigned",
                statusKey: statuses.enrolmentAgreement.statusTextKey,
                ...statuses.enrolmentAgreement,
            },
        ],
    }),

    createCourseSelectionTasks: (statuses, isLockedStatus) => {
        if (isVersionUK) {
            return [{
                titleKey: "tasks.consultationCall",
                statusKey: statuses.consultationCall.statusTextKey,
                ...statuses.consultationCall,
            }];
        }

        const tasks = isLockedStatus.courseSelection || isLockedStatus.consultationCall
            ? [{ titleKey: "tasks.completeAllAvailableTasksToUnlock" }]
            : [{
                titleKey: "tasks.subjectsReviewed",
                statusKey: statuses.courseSelection.statusTextKey,
                ...statuses.courseSelection,
            }];

        if (statuses.consultationCall.key !== KEY_NULL) {
            tasks.push({
                titleKey: "tasks.consultationCall",
                statusKey: statuses.consultationCall.statusTextKey,
                ...statuses.consultationCall,
            });
        }

        return tasks;
    },

    createCourseSelectionTask: (statuses, isLockedStatus) => {
        const courseSelectionTasks = applicantTasksHelper.createCourseSelectionTasks(statuses, isLockedStatus);

        if (isVersionUK) {
            return {
                key: TASK_KEYS.CONSULTATION_CALL,
                titleKey: "navigation.consultationCall",
                isLocked: isLockedStatus.consultationCall,
                isDisabled: isLockedStatus.consultationCall,
                isWarning: statuses.consultationCall.isWarning,
                isSuccess: statuses.consultationCall.key === KEY_NULL ? false : statuses.consultationCall.isSuccess,
                tasks: courseSelectionTasks,
            };
        }

        return {
            key: TASK_KEYS.COURSE_SELECTION,
            titleKey: "navigation.courseSelection",
            isLocked: isLockedStatus.courseSelection || isLockedStatus.consultationCall,
            isDisabled: isLockedStatus.enrolmentAgreement,
            isWarning: statuses.courseSelection.isWarning || statuses.consultationCall.isWarning,
            isSuccess: statuses.consultationCall.key === KEY_NULL
                ? statuses.courseSelection.isSuccess
                : statuses.courseSelection.isSuccess && statuses.consultationCall.isSuccess,
            tasks: courseSelectionTasks,
        };
    },

    createTuitionBillingTask: (statuses, isLockedStatus) => ({
        key: TASK_KEYS.TUITION_BILLING,
        titleKey: "navigation.tuitionBilling",
        icon: IconTuitionBilling,
        isLocked: isLockedStatus.tuitionBilling,
        isDisabled: (isVersionUK ? isLockedStatus.enrolmentAgreement : false) || isLockedStatus.tuitionBilling,
        isWarning: statuses.tuitionBilling.isWarning,
        isSuccess: statuses.tuitionBilling.isSuccess,
        tasks: isLockedStatus.tuitionBilling
            // ? [{ titleKey: "tasks.completeTheEnrolmentAgreementTask" }]
            ? [{ titleKey: "tasks.completeAllAvailableTasksToUnlock" }]
            : [{
                titleKey: "tasks.tuitionPaid",
                statusKey: statuses.tuitionBilling.statusTextKey,
                ...statuses.tuitionBilling,
            }],
    }),

    filterTasksByVersion: (taskPanels, hideSubjectSelectionUS, showConsultationCall, generalStatuses) => {
        const filteredTasks = taskPanels.filter((item) => {
            if (!hideSubjectSelectionUS && showConsultationCall) return true;
            return ![TASK_KEYS.CONSULTATION_CALL, TASK_KEYS.COURSE_SELECTION].includes(item.key);
        });

        if (generalStatuses?.school === "KIH") return filteredTasks;

        return hideSubjectSelectionUS
            ? filteredTasks.filter((item) => !HIDDEN_FOR_US_FULL_TIME.includes(item.key))
            : filteredTasks;
    },

    generateTaskPanels: ({
        statuses,
        isLockedStatus,
        generalStatuses,
        isRequiredDBS,
        isRequiredOutOfYear,
        isSchoolReportsRequired,
        hideSubjectSelectionUS,
        showConsultationCall,
    }) => {
        const taskPanels = [
            applicantTasksHelper.createResponsibleAdultsTask(statuses, isLockedStatus),
            applicantTasksHelper.createStudentDetailsTask(statuses, isLockedStatus, generalStatuses, isRequiredOutOfYear, isRequiredDBS),
            applicantTasksHelper.createPreviousEducationTask(statuses, isLockedStatus, generalStatuses, isSchoolReportsRequired),
            applicantTasksHelper.createCourseSelectionTask(statuses, isLockedStatus),
            applicantTasksHelper.createEnrolmentAgreementTask(statuses, isLockedStatus),
            applicantTasksHelper.createTuitionBillingTask(statuses, isLockedStatus),
        ];

        return applicantTasksHelper.filterTasksByVersion(taskPanels, hideSubjectSelectionUS, showConsultationCall, generalStatuses);
    },
};
